import {
  ComponentRef,
  computed,
  Injectable,
  Injector,
  inputBinding,
  isSignal,
  signal,
  Signal,
  TemplateRef,
  Type,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { LightboxRef } from './lightbox-ref';

/** Content projected into the lightbox's default title/body sections. */
type LightboxContentPrimitive = string | TemplateRef<unknown>;

interface LightboxStateBase {
  isClosable: boolean;
  isOpen: boolean;
  mode: LightboxMode;
}

interface LightboxDefaultContent {
  title?: LightboxContentPrimitive | null;
  body?: LightboxContentPrimitive | null;
}

interface LightboxConfirmationActions {
  positiveLabel: string;
  negativeLabel: string;
}

type LightboxConfirmationContent = Required<LightboxDefaultContent> & LightboxConfirmationActions;

interface LightboxComponentOptions<T> {
  isClosable?: boolean;
  /**
   * Initial input values, keyed by each input's public binding name (i.e. its alias, same as you'd
   * write in a template binding) rather than its class property name, applied via `ComponentRef.setInput`.
   * Pass a `Signal` (instead of its unwrapped value) for any input that should stay in sync as your
   * signal changes over time — e.g. `{ payees: this.$payees }`, not `{ payees: this.$payees() }`.
   */
  inputs?: Record<string, unknown>;
  /** Handlers wired to the dynamically-loaded component's outputs (both `output()` and `@Output()` are supported). */
  outputs?: { [K in keyof T]?: (value: unknown) => void };
  injector?: Injector;
}

type LightboxMode = 'component' | 'default' | 'confirmation' | null;

type LightboxState =
  | (LightboxStateBase & {
      mode: 'default';
    } & LightboxDefaultContent)
  | (LightboxStateBase & {
      mode: 'confirmation';
    } & LightboxConfirmationContent)
  | (LightboxStateBase & {
      mode: null | 'component';
      title?: null;
      body?: null;
    });

export {
  LightboxContentPrimitive,
  LightboxDefaultContent,
  LightboxConfirmationContent,
  LightboxComponentOptions,
  LightboxState
};
export { LightboxRef };

@Injectable({
  providedIn: 'root'
})
export class Lightbox {
  private readonly _$state: WritableSignal<LightboxState> = signal<LightboxState>({
    isClosable: true,
    isOpen: false,
    mode: null
  });
  readonly $state: Signal<LightboxState> = this._$state.asReadonly();
  /** True while the `<dialog oke-lightbox>` is shown, including its closing animation (mode is cleared only in `notifyClosed`). */
  readonly $isOpen: Signal<boolean> = computed(() => this._$state().mode !== null);
  private readonly _$isHidden = signal<boolean>(false);
  /** True while an open lightbox is temporarily hidden via `hide()`; `$isOpen` remains true meanwhile. */
  readonly $isHidden: Signal<boolean> = this._$isHidden.asReadonly();
  private readonly _$isPositiveDismissal = signal<boolean | null>(null);
  readonly $isPositiveDismissal = this._$isPositiveDismissal.asReadonly();
  private readonly _$typeOfDismissal = signal<'forced' | 'optional' | null>(null);
  readonly $typeOfDismissal = this._$typeOfDismissal.asReadonly();
  private readonly _closed = new Subject<boolean>();
  readonly closed$: Observable<boolean> = this._closed.asObservable();
  private readonly _dismissed = new Subject<void>();
  readonly dismissed$: Observable<void> = this._dismissed.asObservable();
  private _outletRef: ViewContainerRef | null = null;
  private _hostElement: HTMLDialogElement | null = null;
  private _componentRef: ComponentRef<unknown> | null = null;
  private _outputSubscriptions: Subscription[] = [];
  private _pendingComponentRequest: (() => void) | null = null;
  private _isDismissalRequested = false;

  /** The registered `<dialog oke-lightbox>` element, if any. */
  get hostElement(): HTMLDialogElement | null {
    return this._hostElement;
  }

  /**
   * @description Registers the single `oke-lightbox` host `<dialog>` element. Called by the
   *   `Lightbox` component itself; not intended for direct consumption elsewhere.
   */
  registerHost(hostElement: HTMLDialogElement | null): void {
    this._hostElement = hostElement;
  }

  /**
   * @description Registers the single `oke-lightbox` host's dynamic-content outlet. Called by the
   *   `Lightbox` component itself; not intended for direct consumption elsewhere.
   */
  registerOutlet(outletRef: ViewContainerRef | null): void {
    this._outletRef = outletRef;
    if (outletRef && this._pendingComponentRequest) {
      const request = this._pendingComponentRequest;
      this._pendingComponentRequest = null;
      request();
    }
  }

  /**
   * @description Opens the lightbox with the simple "title + body" content, each of which may be
   *   plain text, an HTML string, or a `TemplateRef`.
   */
  openDefault(content: LightboxDefaultContent & Partial<Pick<LightboxStateBase, 'isClosable'>>): LightboxRef<unknown> {
    this._destroyDynamicComponent();
    this._$isHidden.set(false);
    this._$state.set({
      isClosable: content.isClosable ?? true,
      isOpen: true,
      mode: 'default',
      title: content.title ?? null,
      body: content.body ?? null
    });
    return new LightboxRef<unknown>(this, () => null);
  }

  /**
   * @description
   */
  openConfirmation(content: Omit<LightboxConfirmationContent, 'isClosable'>): LightboxRef<unknown> {
    this._destroyDynamicComponent();
    this._$isHidden.set(false);
    this._$state.set({
      isClosable: false,
      isOpen: true,
      mode: 'confirmation',
      title: content.title ?? 'Confirmation',
      body: content.body ?? "Let's see…",
      positiveLabel: content.positiveLabel,
      negativeLabel: content.negativeLabel
    });
    return new LightboxRef<unknown>(this, () => null);
  }

  /** @description Opens the lightbox and dynamically loads `component` into its body. */
  openComponent<T>(component: Type<T>, options: LightboxComponentOptions<T> = {}): LightboxRef<T> {
    this._$isHidden.set(false);
    this._$state.set({
      isClosable: options.isClosable ?? true,
      isOpen: true,
      mode: 'component',
      title: null,
      body: null
    });
    const create = (): void => this._createDynamicComponent(component, options);
    if (this._outletRef) {
      create();
    } else {
      this._pendingComponentRequest = create;
    }
    return new LightboxRef<T>(this, () => (this._componentRef?.instance as T) ?? null);
  }

  /** @description Toggles whether the currently-visible (or next-opened) lightbox can be dismissed by the user. */
  setClosable(isClosable: boolean): void {
    this._$state.update(state => ({ ...state, isClosable }));
  }

  /** @description Temporarily hides an open lightbox without altering its content or state. */
  hide(): void {
    if (this._$state().isOpen) {
      this._$isHidden.set(true);
    }
  }

  /** @description Re-reveals a lightbox previously hidden via `hide()`. */
  reveal(): void {
    this._$isHidden.set(false);
  }

  /** @description Programmatically closes the lightbox, regardless of its closable state. */
  close(): void {
    this._requestClose(false);
  }

  /** @description Requests the lightbox be dismissed as though the user triggered it; honors `isClosable`. */
  dismiss(typeOfDismissal: 'forced' | 'optional', isPositiveDismissal: boolean): void {
    this._$typeOfDismissal.set(typeOfDismissal);
    this._$isPositiveDismissal.set(isPositiveDismissal);
    this._requestClose(true);
  }

  /**
   * @description Invoked by the `Lightbox` component once its close fade-out animation finishes.
   *   Not intended for direct consumption elsewhere.
   */
  notifyClosed(): void {
    this._destroyDynamicComponent();
    this._$isHidden.set(false);
    const isDismissed = this._isDismissalRequested;
    this._isDismissalRequested = false;
    this._$state.update(state => {
      return {
        isClosable: state.isClosable,
        isOpen: state.isOpen,
        mode: null
      };
    });
    if (isDismissed) {
      this._dismissed.next();
    }
    this._closed.next(this._$typeOfDismissal() === 'forced' ? (this._$isPositiveDismissal() as boolean) : true);
    this._$isPositiveDismissal.set(null);
    this._$typeOfDismissal.set(null);
  }

  private _requestClose(isDismissed: boolean): void {
    const state = this._$state();
    let isInvalid = false;
    if (state.mode === 'confirmation') {
      isInvalid = !state.isClosable && this._$typeOfDismissal() === 'optional';
    } else if (state.mode === 'default') {
      isInvalid = !state.isClosable;
    }
    if (!state.isOpen || (isDismissed && isInvalid)) {
      return;
    }
    this._isDismissalRequested = isDismissed;
    this._$state.update(currState => ({ ...currState, isOpen: false }));
  }

  private _createDynamicComponent<T>(component: Type<T>, options: LightboxComponentOptions<T>): void {
    if (!this._outletRef) {
      return;
    }
    this._destroyDynamicComponent();
    const bindingInputs = options.inputs
      ? Object.entries(options.inputs).map(([key, value]) =>
          inputBinding(key, isSignal(value) ? value : (): unknown => value)
        )
      : undefined;
    const bindings = bindingInputs ? [...bindingInputs] : undefined;
    const componentRef = this._outletRef.createComponent(component, {
      bindings,
      injector: options.injector
    });
    this._componentRef = componentRef;
    componentRef.location.nativeElement.classList.add('is-projected');
    Object.entries(options.outputs ?? {}).forEach(([key, handler]) => {
      const emitter = (componentRef.instance as Record<string, unknown>)[key];
      if (emitter && typeof (emitter as { subscribe?: unknown }).subscribe === 'function') {
        this._outputSubscriptions.push(
          (emitter as { subscribe: (cb: (value: unknown) => void) => Subscription }).subscribe(
            handler as (value: unknown) => void
          )
        );
      }
    });
    componentRef.changeDetectorRef.detectChanges();
  }

  private _destroyDynamicComponent(): void {
    this._outputSubscriptions.forEach(subscription => subscription.unsubscribe());
    this._outputSubscriptions = [];
    this._componentRef?.destroy();
    this._componentRef = null;
    this._outletRef?.clear();
  }
}
